import React, { useState, useEffect } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useFixtureNotes } from '../context/UserDataContext'

// Check if Clerk is configured
const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

interface FixtureNotesProps {
  fixtureId: number
}

export function FixtureNotes({ fixtureId }: FixtureNotesProps) {
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false }
  const { isSignedIn } = auth
  const { getNote, saveNote, deleteNote } = useFixtureNotes()

  const [isEditing, setIsEditing] = useState(false)
  const [content, setContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const existingNote = getNote(fixtureId)

  // Sync content when fixture changes or note loads
  useEffect(() => {
    setContent(existingNote || '')
    setIsEditing(false)
  }, [fixtureId, existingNote])

  if (!isSignedIn) {
    return null
  }

  const handleSave = async () => {
    if (!content.trim()) {
      // If content is empty, delete the note
      if (existingNote) {
        setIsSaving(true)
        try {
          await deleteNote(fixtureId)
        } catch (err) {
          console.error('Failed to delete note:', err)
        } finally {
          setIsSaving(false)
        }
      }
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await saveNote(fixtureId, content.trim())
      setIsEditing(false)
    } catch (err) {
      console.error('Failed to save note:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setContent(existingNote || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel()
    } else if (e.key === 'Enter' && e.metaKey) {
      handleSave()
    }
  }

  return (
    <div className="fixture-notes">
      <div className="fixture-notes-header">
        <span className="fixture-notes-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          Notes
        </span>
        {!isEditing && (
          <button
            className="fixture-notes-edit-btn"
            onClick={() => setIsEditing(true)}
          >
            {existingNote ? 'Edit' : 'Add'}
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="fixture-notes-editor">
          <textarea
            className="fixture-notes-textarea"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add private notes about this fixture..."
            autoFocus
            rows={3}
          />
          <div className="fixture-notes-actions">
            <button
              className="fixture-notes-cancel"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              className="fixture-notes-save"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
          <div className="fixture-notes-hint">
            Press Cmd+Enter to save, Escape to cancel
          </div>
        </div>
      ) : existingNote ? (
        <div
          className="fixture-notes-content"
          onClick={() => setIsEditing(true)}
        >
          {existingNote}
        </div>
      ) : (
        <div
          className="fixture-notes-empty"
          onClick={() => setIsEditing(true)}
        >
          Click to add notes...
        </div>
      )}
    </div>
  )
}
